export type WorkspaceSelection = {
  activeOrgId: string;
  activeProjectId: string;
  projectIdsByOrganization?: Record<string, string>;
  activeReleaseAppId: string;
  activeReleaseAppVersionId: string;
  activeEnvId: string;
};

export interface ProjectSelectionOption {
  id: string;
  slug: string;
}

function resolveProjectSelectionId<TProject extends ProjectSelectionOption>(
  projects: TProject[],
  routeProjectSlug: string,
  preferredProjectId: string
) {
  return (
    projects.find((project) => project.slug === routeProjectSlug)?.id ||
    projects.find((project) => project.id === preferredProjectId)?.id ||
    projects[0]?.id ||
    ''
  );
}

const WORKSPACE_SELECTION_STORAGE_KEY = 'cindro.admin.workspace-selection';

const EMPTY: WorkspaceSelection = {
  activeOrgId: '',
  activeProjectId: '',
  projectIdsByOrganization: {},
  activeReleaseAppId: '',
  activeReleaseAppVersionId: '',
  activeEnvId: ''
};

export function readWorkspaceSelection(): WorkspaceSelection {
  if (typeof window === 'undefined') {
    return { ...EMPTY };
  }

  try {
    const raw = window.localStorage.getItem(WORKSPACE_SELECTION_STORAGE_KEY);
    if (!raw) {
      return { ...EMPTY };
    }

    const parsed = JSON.parse(raw) as Partial<WorkspaceSelection>;
    const projectIdsByOrganization = Object.fromEntries(
      Object.entries(parsed.projectIdsByOrganization || {}).filter(
        ([organizationId, projectId]) =>
          Boolean(organizationId) && typeof projectId === 'string'
      )
    );
    return {
      activeOrgId: parsed.activeOrgId || '',
      activeProjectId: parsed.activeProjectId || '',
      projectIdsByOrganization,
      activeReleaseAppId: parsed.activeReleaseAppId || '',
      activeReleaseAppVersionId: parsed.activeReleaseAppVersionId || '',
      activeEnvId: parsed.activeEnvId || ''
    };
  } catch {
    return { ...EMPTY };
  }
}

export function writeWorkspaceSelection(selection: WorkspaceSelection) {
  if (typeof window === 'undefined') {
    return;
  }

  const previous = readWorkspaceSelection();
  const projectIdsByOrganization = {
    ...previous.projectIdsByOrganization,
    ...(previous.activeOrgId && previous.activeProjectId
      ? { [previous.activeOrgId]: previous.activeProjectId }
      : {}),
    ...(selection.projectIdsByOrganization || {})
  };
  if (selection.activeOrgId && selection.activeProjectId) {
    projectIdsByOrganization[selection.activeOrgId] = selection.activeProjectId;
  }

  window.localStorage.setItem(
    WORKSPACE_SELECTION_STORAGE_KEY,
    JSON.stringify({ ...selection, projectIdsByOrganization })
  );
}

export function clearWorkspaceSelection() {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(WORKSPACE_SELECTION_STORAGE_KEY);
}

function preferredProjectIdForOrganization(
  selection: WorkspaceSelection,
  organizationId: string
) {
  return (
    selection.projectIdsByOrganization?.[organizationId] ||
    (selection.activeOrgId === organizationId ? selection.activeProjectId : '')
  );
}

export { preferredProjectIdForOrganization, resolveProjectSelectionId };
